import { useState } from 'react';
import { FiClock, FiEdit2, FiTrash2, FiX, FiAlertCircle, FiSend, FiLock, FiMapPin } from 'react-icons/fi';
import { Modal } from '../../design-system';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { timeEntriesAPI, timeEntryModificationsAPI } from '../../services/api';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';

const DayDetailsModal = ({ isOpen, onClose, selectedDate, timeEntries, schedule, onUpdate }) => {
  const { addNotification } = useApp();
  const { user } = useAuth();
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modificationReason, setModificationReason] = useState('');

  // Detectar si NO es admin (empleados y recepcionistas deben solicitar modificaciones)
  const isEmployee = user?.role === 'personal_limpieza' || user?.role === 'recepcionista';

  if (!selectedDate) return null;

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.round((minutes % 1) * 60);
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatHoursMinutes = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const formatDateTimeForInput = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
      const date = parseISO(dateTimeString);
      return format(date, "yyyy-MM-dd'T'HH:mm");
    } catch (error) {
      return '';
    }
  };

  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    return format(now, "yyyy-MM-dd'T'HH:mm");
  };

  const handleEdit = (entry) => {
    // Si el mes ya está validado, no permitir edición
    if (entry.is_month_validated) {
      addNotification({
        type: 'error',
        message: 'No se pueden modificar fichajes de un mes ya validado'
      });
      return;
    }

    // Si ya tiene una solicitud de modificación pendiente, no permitir crear otra
    if (entry.has_pending_modification) {
      addNotification({
        type: 'warning',
        message: 'Ya tienes una solicitud de modificacion pendiente para este fichaje'
      });
      return;
    }

    setEditingEntry({
      ...entry,
      clock_in_time: formatDateTimeForInput(entry.clock_in_time),
      clock_out_time: formatDateTimeForInput(entry.clock_out_time),
      original_clock_in_time: entry.clock_in_time,
      original_clock_out_time: entry.clock_out_time,
      original_notes: entry.notes,
    });
    setModificationReason('');
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;

    // Validate that times are not in the future
    const now = new Date();
    if (editingEntry.clock_in_time && new Date(editingEntry.clock_in_time) > now) {
      addNotification({
        type: 'error',
        message: 'La hora de entrada no puede ser en el futuro'
      });
      return;
    }
    if (editingEntry.clock_out_time && new Date(editingEntry.clock_out_time) > now) {
      addNotification({
        type: 'error',
        message: 'La hora de salida no puede ser en el futuro'
      });
      return;
    }

    // Validate that clock_out is not before clock_in
    if (editingEntry.clock_in_time && editingEntry.clock_out_time) {
      const clockIn = new Date(editingEntry.clock_in_time);
      const clockOut = new Date(editingEntry.clock_out_time);
      if (clockOut < clockIn) {
        addNotification({
          type: 'error',
          message: 'La hora de salida no puede ser anterior a la hora de entrada'
        });
        return;
      }
    }

    setIsSubmitting(true);

    // Si es empleado, crear solicitud de modificacion en lugar de editar directamente
    if (isEmployee) {
      try {
        // Detectar qué cambios se están solicitando
        const originalClockIn = editingEntry.original_clock_in_time;
        const originalClockOut = editingEntry.original_clock_out_time;
        const newClockIn = editingEntry.clock_in_time ? new Date(editingEntry.clock_in_time).toISOString() : null;
        const newClockOut = editingEntry.clock_out_time ? new Date(editingEntry.clock_out_time).toISOString() : null;

        // Solo incluir campos que realmente cambiaron
        const requestData = {
          time_entry_id: editingEntry.id,
          reason: modificationReason || null,
        };

        // Comparar si cambió la entrada
        if (newClockIn !== originalClockIn) {
          requestData.requested_clock_in_time = newClockIn;
        }

        // Comparar si cambió la salida
        if (newClockOut !== originalClockOut) {
          requestData.requested_clock_out_time = newClockOut;
        }

        // Comparar si cambiaron las notas
        if (editingEntry.notes !== editingEntry.original_notes) {
          requestData.requested_notes = editingEntry.notes || null;
        }

        // Verificar que hay al menos un cambio
        if (!requestData.requested_clock_in_time &&
            !requestData.requested_clock_out_time &&
            requestData.requested_notes === undefined) {
          addNotification({
            type: 'warning',
            message: 'No has realizado ningun cambio'
          });
          setIsSubmitting(false);
          return;
        }

        await timeEntryModificationsAPI.create(requestData);
        addNotification({
          type: 'success',
          message: 'Solicitud de modificacion enviada correctamente. Un administrador la revisara.'
        });
        setEditingEntry(null);
        setModificationReason('');
        onUpdate();
      } catch (error) {
        console.error('Error creating modification request:', error);
        addNotification({
          type: 'error',
          message: error.response?.data?.detail || 'Error al enviar la solicitud de modificacion'
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Para administradores: edicion directa
    try {
      const updateData = {
        clock_in_time: editingEntry.clock_in_time ? new Date(editingEntry.clock_in_time).toISOString() : null,
        clock_out_time: editingEntry.clock_out_time ? new Date(editingEntry.clock_out_time).toISOString() : null,
        notes: editingEntry.notes || null
      };

      await timeEntriesAPI.update(editingEntry.id, updateData);
      addNotification({
        type: 'success',
        message: 'Registro actualizado correctamente'
      });
      setEditingEntry(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating time entry:', error);
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Error al actualizar el registro'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteEntry) return;

    setIsSubmitting(true);
    try {
      await timeEntriesAPI.delete(deleteEntry.id);
      addNotification({
        type: 'success',
        message: 'Registro eliminado correctamente'
      });
      setDeleteEntry(null);
      onUpdate();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Error al eliminar el registro'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalMinutes = timeEntries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0);

  return (
    <>
      <Modal
        isOpen={isOpen && !editingEntry && !deleteEntry}
        onClose={onClose}
        title={`Fichajes - ${format(selectedDate, 'dd/MM/yyyy', { locale: es })}`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Header with schedule and total time */}
          <div className="grid grid-cols-2 gap-3">
            {schedule && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-xs text-blue-600 mb-1">Cuadrante asignado</p>
                <p className="text-2xl font-bold text-blue-900">{formatHoursMinutes(schedule.hours)}</p>
                {schedule.shift && (
                  <p className="text-xs text-blue-700 mt-1">{schedule.shift}</p>
                )}
              </div>
            )}
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-600 mb-1">Tiempo trabajado</p>
              <p className="text-2xl font-bold text-green-900">{formatHoursMinutes(totalMinutes / 60)}</p>
              <p className="text-xs text-green-700 mt-1">{timeEntries.length} fichaje{timeEntries.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Schedule notes */}
          {schedule?.notes && (
            <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
              <p className="text-xs text-blue-700">
                <span className="font-medium">Nota:</span> {schedule.notes}
              </p>
            </div>
          )}

          {/* Time Entries List */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Tus fichajes de este día</h3>

            {timeEntries.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FiClock className="mx-auto text-gray-400 text-5xl mb-3" />
                <p className="text-gray-600 font-medium">No hay fichajes registrados</p>
                <p className="text-gray-500 text-sm mt-1">Este día no tienes registros de entrada/salida</p>
              </div>
            ) : (
              <div className="space-y-2">
                {timeEntries.map((entry, index) => (
                  <div
                    key={entry.id}
                    className={`bg-gradient-to-r from-white to-gray-50 border rounded-lg p-4 hover:shadow-lg transition-all ${
                      entry.is_month_validated
                        ? 'border-green-200 bg-green-50'
                        : entry.has_pending_modification
                        ? 'border-orange-200'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          Fichaje #{index + 1}
                        </span>
                        {entry.is_month_validated && (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                            <FiLock className="h-3 w-3" />
                            Validado
                          </span>
                        )}
                        {entry.has_pending_modification && (
                          <span className="inline-flex items-center gap-1 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                            <FiAlertCircle className="h-3 w-3" />
                            Solicitud pendiente
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        {!entry.is_month_validated && !entry.has_pending_modification && (
                          <>
                            <button
                              onClick={() => handleEdit(entry)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                              title={isEmployee ? "Solicitar modificacion" : "Editar fichaje"}
                            >
                              {isEmployee ? <FiSend className="h-5 w-5" /> : <FiEdit2 className="h-5 w-5" />}
                            </button>
                            {!isEmployee && (
                              <button
                                onClick={() => setDeleteEntry(entry)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Eliminar fichaje"
                              >
                                <FiTrash2 className="h-5 w-5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Entrada</p>
                        <p className="text-lg font-bold text-gray-900">
                          {entry.clock_in_time ? format(parseISO(entry.clock_in_time), 'HH:mm', { locale: es }) : '-'}
                        </p>
                        <div className="flex items-center justify-center gap-1">
                          <p className="text-xs text-gray-500">
                            {entry.clock_in_time ? format(parseISO(entry.clock_in_time), 'ss', { locale: es }) + 's' : ''}
                          </p>
                          {entry.clock_in_latitude && entry.clock_in_longitude && (
                            <FiMapPin className="h-3 w-3 text-green-500" title={`Ubicación: ${entry.clock_in_latitude.toFixed(4)}, ${entry.clock_in_longitude.toFixed(4)}`} />
                          )}
                        </div>
                      </div>

                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Salida</p>
                        {entry.clock_out_time ? (
                          <>
                            <p className="text-lg font-bold text-gray-900">
                              {format(parseISO(entry.clock_out_time), 'HH:mm', { locale: es })}
                            </p>
                            <div className="flex items-center justify-center gap-1">
                              <p className="text-xs text-gray-500">
                                {format(parseISO(entry.clock_out_time), 'ss', { locale: es })}s
                              </p>
                              {entry.clock_out_latitude && entry.clock_out_longitude && (
                                <FiMapPin className="h-3 w-3 text-green-500" title={`Ubicación: ${entry.clock_out_latitude.toFixed(4)}, ${entry.clock_out_longitude.toFixed(4)}`} />
                              )}
                            </div>
                          </>
                        ) : (
                          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            En curso
                          </span>
                        )}
                      </div>

                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Duración</p>
                        <p className="text-lg font-bold text-blue-600">
                          {formatDuration(entry.total_minutes)}
                        </p>
                      </div>
                    </div>

                    {entry.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-600">
                          <span className="font-semibold">Nota:</span> {entry.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status message */}
          {schedule && timeEntries.length > 0 && (
            <div className={`rounded-lg p-3 border ${
              (totalMinutes / 60) >= schedule.hours
                ? 'bg-green-50 border-green-200'
                : 'bg-orange-50 border-orange-200'
            }`}>
              <p className={`text-sm font-medium text-center ${
                (totalMinutes / 60) >= schedule.hours ? 'text-green-700' : 'text-orange-700'
              }`}>
                {(totalMinutes / 60) >= schedule.hours ? (
                  <>✓ Has cumplido con las horas asignadas de este día</>
                ) : (
                  <>⚠️ Te faltan {formatHoursMinutes(schedule.hours - (totalMinutes / 60))} para completar el cuadrante</>
                )}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Modal / Modification Request Modal */}
      {editingEntry && (
        <Modal
          isOpen={true}
          onClose={() => {
            setEditingEntry(null);
            setModificationReason('');
          }}
          title={isEmployee ? "Solicitar Modificacion de Fichaje" : "Editar Registro"}
          size="md"
        >
          <div className="space-y-4">
            {isEmployee && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <FiAlertCircle className="inline mr-2" />
                  Los cambios que realices seran enviados como solicitud de modificacion. Un administrador debera aprobarlos.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora de Entrada
              </label>
              <input
                type="datetime-local"
                value={editingEntry.clock_in_time}
                max={getCurrentDateTimeLocal()}
                onChange={(e) => setEditingEntry({ ...editingEntry, clock_in_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hora de Salida
              </label>
              <input
                type="datetime-local"
                value={editingEntry.clock_out_time}
                max={getCurrentDateTimeLocal()}
                onChange={(e) => setEditingEntry({ ...editingEntry, clock_out_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas
              </label>
              <textarea
                value={editingEntry.notes || ''}
                onChange={(e) => setEditingEntry({ ...editingEntry, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Añade notas opcionales..."
              />
            </div>

            {isEmployee && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo de la solicitud
                </label>
                <textarea
                  value={modificationReason}
                  onChange={(e) => setModificationReason(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Explica por que necesitas modificar este fichaje..."
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setEditingEntry(null);
                  setModificationReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  isEmployee
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
                disabled={isSubmitting}
              >
                {isEmployee && <FiSend className="h-4 w-4" />}
                {isSubmitting
                  ? (isEmployee ? 'Enviando...' : 'Guardando...')
                  : (isEmployee ? 'Enviar Solicitud' : 'Guardar')
                }
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteEntry && (
        <Modal
          isOpen={true}
          onClose={() => setDeleteEntry(null)}
          title="Eliminar Registro"
          size="sm"
        >
          <div>
            <p className="text-gray-600 mb-4">
              ¿Estás seguro de que deseas eliminar este registro de fichaje?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Fecha: {deleteEntry.clock_in_time ? format(parseISO(deleteEntry.clock_in_time), 'dd/MM/yyyy HH:mm', { locale: es }) : '-'}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteEntry(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default DayDetailsModal;
